import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BugReportIcon from '@mui/icons-material/BugReport';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CodeIcon from '@mui/icons-material/Code';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import { Link } from 'react-router-dom';

const QualityAssurance: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Define QA practices
  const qaPractices = [
    {
      title: "Test Automation",
      description: "Accelerate testing cycles with automated test scripts that ensure consistent, repeatable verification of application functionality.",
      icon: <CodeIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Performance Testing",
      description: "Verify your application's speed, stability, and scalability under various load conditions to prevent performance bottlenecks.",
      icon: <SpeedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Security Testing",
      description: "Identify vulnerabilities in your applications with comprehensive security assessments and penetration testing.",
      icon: <SecurityIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Mobile Testing",
      description: "Ensure optimal user experience across multiple devices, operating systems, and screen sizes.",
      icon: <MobileFriendlyIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
  ];
  
  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Quality Assurance Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive testing and quality management to deliver flawless digital experiences
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Excellence in Software Quality
              </Typography>
              <Typography paragraph>
                In today's digital landscape, software quality is not merely a technical
                consideration—it's a critical business differentiator. At Right Track Technologies Limited,
                we offer comprehensive Quality Assurance and Testing services that ensure your applications
                meet the highest standards of quality, reliability, and user experience.
              </Typography>
              <Typography paragraph>
                Our experienced QA team employs industry-leading methodologies, tools, and best practices 
                to identify defects early in the development lifecycle, reducing costs and accelerating 
                time-to-market. We partner with your development teams to implement quality gates and 
                continuous testing processes that build quality into every stage of your software delivery.
              </Typography>
              <Typography paragraph>
                Whether you need comprehensive testing for a new application, assistance with test 
                automation, or a complete quality transformation, our tailored QA services will help you 
                deliver exceptional digital experiences that build customer trust and loyalty.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1581092335878-2d9ff86ca2bf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Quality Assurance"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Quality Assurance Practices Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our Quality Assurance Practices
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              We implement proven QA practices to deliver exceptional software quality
            </Typography>
          </motion.div>

          <Box 
            sx={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              justifyContent: 'center',
            }}
          >
            {qaPractices.map((practice, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ width: isMobile ? '100%' : 'calc(50% - 16px)' }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {practice.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    {practice.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {practice.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Our Quality Assurance Services
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            Comprehensive QA solutions tailored to your specific needs
          </Typography>
        </motion.div>
        
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 4,
          justifyContent: 'center'
        }}>
          {[
            {
              title: "QA Consulting & Strategy",
              description: "Expert guidance to establish or enhance your QA processes, test strategies, and quality management frameworks.",
              icon: <SettingsIcon fontSize="large" />,
            },
            {
              title: "Functional Testing",
              description: "Thorough verification that your application works as expected across all features and user scenarios.",
              icon: <VerifiedUserIcon fontSize="large" />,
            },
            {
              title: "Test Automation",
              description: "Development and implementation of automated test frameworks to increase test coverage and efficiency.",
              icon: <IntegrationInstructionsIcon fontSize="large" />,
            },
            {
              title: "Performance Testing",
              description: "Rigorous assessment of system performance, load capacity, and scalability to identify bottlenecks.",
              icon: <SpeedIcon fontSize="large" />,
            },
            {
              title: "Security Testing",
              description: "Comprehensive security assessments and penetration testing to identify and address vulnerabilities.",
              icon: <SecurityIcon fontSize="large" />,
            },
            {
              title: "API & Integration Testing",
              description: "Verification of seamless interactions between system components and third-party integrations.",
              icon: <AccountTreeIcon fontSize="large" />,
            },
          ].map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box sx={{ color: '#0c2340', mb: 2 }}>
                  {service.icon}
                </Box>
                <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                  {service.title}
                </Typography>
                <Typography color="text.secondary" sx={{ flex: 1 }}>
                  {service.description}
                </Typography>
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* QA Methodology Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our QA Methodology
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              A structured approach to software quality assurance
            </Typography>
          </motion.div>

          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 3,
              mb: 8
            }}
          >
            {[
              {
                step: "1",
                title: "Requirements Analysis",
                description: "Review specifications to understand functionality, performance criteria, and quality objectives.",
              },
              {
                step: "2",
                title: "Test Planning",
                description: "Develop comprehensive test strategies, plans, and test cases based on requirements.",
              },
              {
                step: "3",
                title: "Test Execution",
                description: "Perform planned tests, both manual and automated, across different environments.",
              },
              {
                step: "4",
                title: "Defect Management",
                description: "Document, track, and verify fixes for identified issues using structured defect management.",
              },
              {
                step: "5",
                title: "Reporting & Analytics",
                description: "Provide detailed test results, metrics, and quality insights to stakeholders.",
              },
            ].map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ flex: 1 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: '#0c2340',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {phase.step}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                    {phase.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {phase.description}
                  </Typography>
                  
                  {/* Connector line */}
                  {index < 4 && !isMobile && (
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        top: 30,
                        right: -16,
                        width: 32,
                        height: 2,
                        bgcolor: '#0c2340',
                        zIndex: 0
                      }} 
                    />
                  )}
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Benefits of Our QA Services
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            Why our clients trust us with their software quality needs
          </Typography>
        </motion.div>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <List>
              {[
                "Reduced time-to-market through early defect detection",
                "Lower development costs by preventing expensive late-stage fixes",
                "Increased customer satisfaction with reliable, user-friendly applications",
                "Greater test coverage with optimal manual and automated testing mix",
                "Improved release confidence with comprehensive test reports"
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ListItem sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckCircleOutlineIcon sx={{ color: '#0c2340' }} />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <List>
              {[
                "Enhanced security and protection of sensitive data",
                "Scalable testing processes that grow with your business",
                "Improved application performance and user experience",
                "Consistent quality across all platforms and devices",
                "Reduced business risk with thorough regression testing"
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ListItem sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckCircleOutlineIcon sx={{ color: '#0c2340' }} />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
        </Box>
      </Container>

      {/* Tools & Technologies Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Tools & Technologies
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              We leverage industry-leading tools to deliver exceptional QA services
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 4,
            justifyContent: 'center'
          }}>
            {[
              {
                category: "Test Management",
                tools: "Jira, TestRail, qTest, Zephyr",
              },
              {
                category: "Automation",
                tools: "Selenium, Appium, Cypress, TestComplete, Katalon",
              },
              {
                category: "Performance",
                tools: "JMeter, LoadRunner, Gatling, K6",
              },
              {
                category: "API Testing",
                tools: "Postman, SoapUI, REST Assured, Karate",
              },
              {
                category: "Security Testing",
                tools: "OWASP ZAP, Burp Suite, Acunetix",
              },
              {
                category: "CI/CD Integration",
                tools: "Jenkins, GitLab CI, GitHub Actions, Azure DevOps",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom 
                    fontWeight="bold"
                    color="#0c2340"
                  >
                    {item.category}
                  </Typography>
                  <Typography color="text.secondary">
                    {item.tools}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box sx={{ bgcolor: '#0c2340', color: 'white', py: 10 }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: 'center' }}
          >
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Elevate Your Software Quality
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9 }}>
              Contact us to discuss how our quality assurance services can help you deliver exceptional digital experiences.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              color="primary"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#0c2340',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              Get Started
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default QualityAssurance; 